/* oxlint-disable jsx-a11y/prefer-tag-over-role -- a CSS-grid table (rounded body inset into the header band,
   sticky header, per-row grid templates) is built from divs with explicit ARIA table roles, as ARIA in HTML allows. */
import { Fragment, useState } from 'react';
import { RiArrowDownLine, RiArrowDownSLine, RiArrowUpLine, RiExpandUpDownLine, RiMoreLine, RiTableLine } from '@remixicon/react';
import { cn } from '@/shared/lib/cn';
import { cx } from './lib/tv';
import { Avatar } from './avatar';
import { Badge } from './badge';
import { Checkbox } from './checkbox';
import { EmptyState } from './empty-state';
import { IconButton } from './icon-button';
import { type ChipColor } from './lib/colors';
import { Menu, type MenuItemSpec } from './menu';
import { Skeleton } from './skeleton';

export interface DataTableColumn<Row> {
  id: string;
  header: React.ReactNode;
  /** Width in design px, or `fill` (shares the remaining width). Default `fill`. */
  width?: number | 'fill';
  /** Minimum width of a `fill` column in design px. Default 0. */
  minWidth?: number;
  align?: 'left' | 'center' | 'right';
  cell: (row: Row, index: number) => React.ReactNode;
  /**
   * Text style (§6.11): `default` 400 14/20 white · `primary` 500 14/20 white (name cells) ·
   * `secondary` 400 14/20 White/60 (dates, roles).
   */
  tone?: 'default' | 'primary' | 'secondary';
  /** Header becomes a sort button (controlled through `sort` / `onSortChange`). */
  sortable?: boolean;
  className?: string;
  headerClassName?: string;
}

export interface DataTableSort {
  columnId: string;
  direction: 'asc' | 'desc';
}

export interface DataTableGroup<Row> {
  id: string;
  label: React.ReactNode;
  count?: number;
  rows: readonly Row[];
  defaultOpen?: boolean;
}

export interface DataTableProps<Row> {
  columns: readonly DataTableColumn<Row>[];
  rows?: readonly Row[];
  /** Grouped rows with collapsible "UMUMIY 6" headers (556:16851) — alternative to `rows`. */
  groups?: readonly DataTableGroup<Row>[];
  getRowId: (row: Row, index: number) => string;
  /**
   * № column, 50 wide: `true` → header "№" and 1-based numbers; or customise ("T.r.", "1."). `align: 'center'` centres the
   * numbers (documents 351:6386 — the header stays left).
   */
  indexColumn?: boolean | { header?: React.ReactNode; format?: (index: number) => React.ReactNode; align?: 'left' | 'center' };
  /** "Harakat" column (100 wide) with a `more-line` menu per row. */
  rowActions?: (row: Row, index: number) => readonly MenuItemSpec[];
  actionsHeader?: React.ReactNode;
  actionsLabel?: string;
  loading?: boolean;
  skeletonRows?: number;
  /** Shown in the body when there are no rows (default: EmptyState "Ma'lumot yo'q"). */
  empty?: React.ReactNode;
  /** Checkbox column (controlled). */
  selection?: { selected: ReadonlySet<string>; onChange: (next: Set<string>) => void };
  onRowClick?: (row: Row, index: number) => void;
  /** Highlighted row (`bg-selected`). */
  activeRowId?: string;
  sort?: DataTableSort | null;
  onSortChange?: (sort: DataTableSort | null) => void;
  /** Even rows White/3. Default true. */
  striped?: boolean;
  /** White/10 line under each row. Default true; the chart readings table (565:24468) has zebra only. */
  divided?: boolean;
  /** Header sticks to the top of the scroll area. Default true. */
  stickyHeader?: boolean;
  /** `compact` ("Ixcham jadval", §9 Q15): rows h28, padding 4/12, 13/18 text. */
  density?: 'default' | 'compact';
  /** Horizontal scroll below this width (design px). */
  minWidth?: number;
  'aria-label'?: string;
  className?: string;
}

/** Interactive targets inside a row that must not trigger `onRowClick`. */
const ROW_CLICK_IGNORE = 'button, a, input, textarea, select, label, [role="checkbox"], [role="menu"], [role="menuitem"], [role="dialog"]';

const ALIGN = { left: 'justify-start text-left', center: 'justify-center text-center', right: 'justify-end text-right' } as const;
const TONE = { default: 'text-fg', primary: 'font-medium text-fg', secondary: 'text-fg-secondary' } as const;
const rem = (px: number) => `${px / 16}rem`;

/**
 * DataTable — spec §6.11 (497:21046, 577:13967): accent/20 frame (1px) + header band h34 (`t-table-head`, 500 16/18
 * accent); body `bg-panel` r12 with shadow-xs; rows h36 + White/10 line, even rows White/3; cells padding 8/12.
 * № = 50 fixed, data columns `1fr`, "Harakat" = 100 fixed.
 */
export function DataTable<Row>({
  columns,
  rows = [],
  groups,
  getRowId,
  indexColumn,
  rowActions,
  actionsHeader = 'Harakat',
  actionsLabel = 'Amallar',
  loading = false,
  skeletonRows = 6,
  empty,
  selection,
  onRowClick,
  activeRowId,
  sort,
  onSortChange,
  striped = true,
  divided = true,
  stickyHeader = true,
  density = 'default',
  minWidth,
  'aria-label': ariaLabel,
  className,
}: DataTableProps<Row>) {
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(
    () => new Set((groups ?? []).filter((g) => g.defaultOpen === false).map((g) => g.id)),
  );
  const compact = density === 'compact';
  const index = indexColumn ? (indexColumn === true ? {} : indexColumn) : null;

  const template = [
    selection ? rem(44) : null,
    index ? rem(50) : null,
    ...columns.map((c) =>
      typeof c.width === 'number' ? rem(c.width) : `minmax(${c.minWidth ? rem(c.minWidth) : '0'}, 1fr)`,
    ),
    rowActions ? rem(100) : null,
  ]
    .filter(Boolean)
    .join(' ');

  const allRows = groups ? groups.flatMap((g) => g.rows) : rows;
  const allIds = allRows.map((r, i) => getRowId(r, i));
  const selectedCount = selection ? allIds.filter((id) => selection.selected.has(id)).length : 0;

  const cellPad = compact ? 'px-12 py-4' : 'px-12 py-8';
  const rowText = compact ? 'text-13 leading-18' : 'text-sm';

  const toggleSort = (columnId: string) => {
    if (!onSortChange) return;
    if (sort?.columnId !== columnId) onSortChange({ columnId, direction: 'asc' });
    else if (sort.direction === 'asc') onSortChange({ columnId, direction: 'desc' });
    else onSortChange(null);
  };

  const header = (
    <div
      role="rowgroup"
      className={cn(
        stickyHeader && 'sticky top-0 z-10',
        // opaque band = accent/20 over the panel, so scrolled rows never show through
        'bg-panel bg-[image:linear-gradient(var(--color-table-head),var(--color-table-head))]',
      )}
    >
      <div role="row" className="grid min-h-34" style={{ gridTemplateColumns: template }}>
        {selection && (
          <div role="columnheader" className="flex items-center px-12">
            <Checkbox
              aria-label="Hammasini belgilash"
              checked={selectedCount === 0 ? false : selectedCount === allIds.length ? true : 'indeterminate'}
              onCheckedChange={(checked) => selection.onChange(checked ? new Set(allIds) : new Set())}
            />
          </div>
        )}
        {index && (
          <div role="columnheader" className="flex items-center gap-4 px-12 py-8 t-table-head">
            {index.header ?? '№'}
          </div>
        )}
        {columns.map((column) => {
          const sorted = sort?.columnId === column.id ? sort.direction : undefined;
          const content = (
            <>
              <span className="truncate">{column.header}</span>
              {column.sortable &&
                (sorted === 'asc' ? (
                  <RiArrowUpLine aria-hidden className="size-16 shrink-0" />
                ) : sorted === 'desc' ? (
                  <RiArrowDownLine aria-hidden className="size-16 shrink-0" />
                ) : (
                  <RiExpandUpDownLine aria-hidden className="size-16 shrink-0 opacity-50" />
                ))}
            </>
          );
          return (
            <div
              key={column.id}
              role="columnheader"
              aria-sort={sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : column.sortable ? 'none' : undefined}
              className={cn('flex min-w-0 items-center gap-4 px-12 py-8 t-table-head', ALIGN[column.align ?? 'left'], column.headerClassName)}
            >
              {column.sortable ? (
                <button
                  type="button"
                  onClick={() => toggleSort(column.id)}
                  className="-mx-4 flex min-w-0 cursor-pointer items-center gap-4 rounded-4 px-4 hover:bg-accent-soft"
                >
                  {content}
                </button>
              ) : (
                content
              )}
            </div>
          );
        })}
        {rowActions && (
          <div role="columnheader" className="flex items-center px-12 py-8 t-table-head">
            {actionsHeader}
          </div>
        )}
      </div>
    </div>
  );

  /** `i` — index in the data (numbering, ids); `visual` — position among rendered rows (zebra). */
  const renderRow = (row: Row, i: number, visual: number) => {
    const id = getRowId(row, i);
    const even = visual % 2 === 1;
    const active = activeRowId === id;
    const selected = selection?.selected.has(id) ?? false;
    return (
      <div
        key={id}
        role="row"
        aria-selected={selection ? selected : undefined}
        tabIndex={onRowClick ? 0 : undefined}
        onClick={
          onRowClick
            ? (e) => {
                // clicks on the checkbox, the row menu (portalled, bubbles through React) or links/buttons in cells
                if ((e.target as HTMLElement).closest(ROW_CLICK_IGNORE)) return;
                onRowClick(row, i);
              }
            : undefined
        }
        onKeyDown={
          onRowClick
            ? (e) => {
                if (e.key === 'Enter' && e.target === e.currentTarget) onRowClick(row, i);
              }
            : undefined
        }
        className={cn(
          // inside stroke like Figma (doesn't add to the 36 px row height)
          'grid items-stretch outline-none',
          divided && 'shadow-[inset_0_-1px_0_var(--color-line)] last:shadow-none',
          compact ? 'min-h-28' : 'min-h-36',
          rowText,
          striped && even && 'bg-zebra',
          (active || selected) && 'bg-selected',
          onRowClick && 'cursor-pointer hover:bg-hover focus-visible:bg-hover focus-visible:shadow-[inset_0_0_0_1px_var(--color-focus)]',
        )}
        style={{ gridTemplateColumns: template }}
      >
        {selection && (
          <div role="cell" className="flex items-center px-12">
            <Checkbox
              aria-label="Qatorni belgilash"
              checked={selected}
              onCheckedChange={(checked) => {
                const next = new Set(selection.selected);
                if (checked) next.add(id);
                else next.delete(id);
                selection.onChange(next);
              }}
            />
          </div>
        )}
        {index && (
          <div role="cell" className={cn('flex items-center text-fg-nav tabular-nums', index.align === 'center' && 'justify-center', cellPad)}>
            {index.format ? index.format(i) : i + 1}
          </div>
        )}
        {columns.map((column) => (
          <div
            key={column.id}
            role="cell"
            className={cn('flex min-w-0 items-center gap-8', cellPad, TONE[column.tone ?? 'default'], ALIGN[column.align ?? 'left'], column.className)}
          >
            {column.cell(row, i)}
          </div>
        ))}
        {rowActions && (
          <div role="cell" className="flex items-center px-12">
            <Menu
              trigger={
                <IconButton icon={RiMoreLine} size="sm" aria-label={actionsLabel} className="-ml-2 [&>svg]:size-20" />
              }
              items={rowActions(row, i)}
              align="start"
            />
          </div>
        )}
      </div>
    );
  };

  let body: React.ReactNode;
  if (loading) {
    body = Array.from({ length: skeletonRows }, (_, r) => (
      <div
        key={r}
        role="row"
        aria-hidden
        className={cn(
          'grid items-center shadow-[inset_0_-1px_0_var(--color-line)] last:shadow-none',
          compact ? 'min-h-28' : 'min-h-36',
          striped && r % 2 === 1 && 'bg-zebra',
        )}
        style={{ gridTemplateColumns: template }}
      >
        {template.split(' ').map((_, c) => (
          <div key={c} className={cellPad}>
            <Skeleton className={cn('h-12', c === 0 ? 'w-24' : 'w-3/5')} />
          </div>
        ))}
      </div>
    ));
  } else if (allRows.length === 0) {
    body = (
      <div role="row" className="flex justify-center px-16 py-40">
        <div role="cell">{empty ?? <EmptyState icon={RiTableLine} title="Ma'lumot yo'q" />}</div>
      </div>
    );
  } else if (groups) {
    // data offsets of each group and the zebra position of its first visible row
    const starts = groups.map((_, g) => groups.slice(0, g).reduce((n, prev) => n + prev.rows.length, 0));
    const visualStarts = groups.map((_, g) =>
      groups.slice(0, g).reduce((n, prev) => n + (collapsed.has(prev.id) ? 0 : prev.rows.length), 0),
    );
    body = groups.map((group, g) => {
      const open = !collapsed.has(group.id);
      const start = starts[g]!;
      const visualStart = visualStarts[g]!;
      return (
        <Fragment key={group.id}>
          <div role="row" className="bg-subtle shadow-[inset_0_-1px_0_var(--color-line)]">
            <div role="cell" className="flex min-h-34 items-center">
              <button
                type="button"
                aria-expanded={open}
                onClick={() =>
                  setCollapsed((prev) => {
                    const next = new Set(prev);
                    if (open) next.add(group.id);
                    else next.delete(group.id);
                    return next;
                  })
                }
                className="flex h-full w-full cursor-pointer items-center gap-8 px-12 text-left"
              >
                <RiArrowDownSLine aria-hidden className={cn('size-16 text-icon transition-transform duration-150', !open && '-rotate-90')} />
                <span className="t-caps">{group.label}</span>
                <Badge size="sm" color="neutral">
                  {group.count ?? group.rows.length}
                </Badge>
              </button>
            </div>
          </div>
          {open && group.rows.map((row, i) => renderRow(row, start + i, visualStart + i))}
        </Fragment>
      );
    });
  } else {
    body = rows.map((row, i) => renderRow(row, i, i));
  }

  return (
    <div role="table" aria-label={ariaLabel} aria-busy={loading || undefined} className={cx('flex min-h-0 flex-col rounded-12 bg-table-head p-px', className)}>
      <div className="min-h-0 flex-1 overflow-auto rounded-[0.6875rem]">
        <div style={minWidth ? { minWidth: rem(minWidth) } : undefined}>
          {header}
          <div role="rowgroup" className="overflow-hidden rounded-12 bg-panel shadow-xs">
            {body}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------------------------------
 * Cell helpers
 * --------------------------------------------------------------------------------------------- */

/** Name cell with a 20 px solid initial avatar (497:21534): gap 8, 500 14/20 white. */
export function AvatarNameCell({ name, src, color = 'auto', subtitle }: { name: string; src?: string | null; color?: ChipColor | 'auto'; subtitle?: React.ReactNode }) {
  return (
    <span className="flex min-w-0 items-center gap-8">
      <Avatar size={20} name={name} src={src} color={color} />
      <span className="flex min-w-0 flex-col">
        <span className="truncate font-medium text-fg">{name}</span>
        {subtitle && <span className="truncate text-xs text-fg-secondary">{subtitle}</span>}
      </span>
    </span>
  );
}
